import { EntityMutationEvent } from '../core/entity-mutations/entity-mutation.model';

// What a successful tool result should do to the rest of the app: mutation events to publish on
// the EntityMutationBus so open pages refetch, plus optional router commands (e.g. jump to a
// campaign the agent just created).
export interface ToolMutationOutcome {
  events: EntityMutationEvent[];
  navigateTo?: (string | number)[];
}

type ToolResultMapper = (
  content: unknown,
  siteId: number | undefined,
  input: unknown,
) => ToolMutationOutcome | undefined;

export function mapToolResultToMutations(
  toolName: string,
  content: unknown,
  siteId: number | undefined,
  input?: unknown,
): ToolMutationOutcome | undefined {
  return TOOL_RESULT_MUTATIONS[toolName]?.(content, siteId, input);
}

// Declarative tool -> mutation mapping covering the mutating tools in the agent's catalog.
// Read-only and unknown tools are simply absent, which makes them no-ops. All parsing is defensive
// against `unknown`: a mapper that cannot extract an id returns undefined (no event beats a
// garbage event).
const TOOL_RESULT_MUTATIONS: Record<string, ToolResultMapper> = {
  campaigns_publish: (content, siteId) => campaignStatusOutcome(content, siteId, 'published'),
  campaigns_unpublish: (content, siteId) => campaignStatusOutcome(content, siteId, 'unpublished'),
  campaigns_updateSettings: campaignSettingsOutcome,
  campaigns_updateTargeting: campaignSettingsOutcome,
  campaigns_updateText: campaignSettingsOutcome,
  campaigns_duplicateWidget: (content, siteId) => {
    const campaign = nestedCampaign(content);
    if (campaign === undefined) {
      return undefined;
    }
    return {
      events: [campaignEvent('updated', campaign.id, siteId, campaign, correlationId(content))],
    };
  },
  campaigns_createFromTemplate: (content, siteId) => {
    const campaign = nestedCampaign(content);
    if (campaign === undefined) {
      return undefined;
    }
    return {
      events: [campaignEvent('created', campaign.id, siteId, campaign, correlationId(content))],
      navigateTo: campaignEditorOrSettings(campaign),
    };
  },
  campaigns_copy: (content, siteId, input) => {
    const events = copyTargets(content)
      .filter((target) => !target.errorCode && typeof target.copiedCampaignId === 'number')
      .map((target) =>
        campaignEvent(
          'created',
          target.copiedCampaignId as number,
          target.targetSiteId,
          undefined,
          correlationId(content),
        ),
      );
    const sourceCampaignId = stoppedSourceCampaignId(content, input);
    if (sourceCampaignId !== undefined) {
      events.push(campaignEvent('unpublished', sourceCampaignId, siteId, undefined, correlationId(content)));
    }
    if (events.length === 0) {
      return undefined;
    }
    return { events };
  },
  campaigns_applyChange: (content, siteId, input) =>
    (content as { isApplied?: unknown } | null)?.isApplied === true
      ? calledCampaignOutcome(content, siteId, input)
      : undefined,
  campaigns_restoreVersion: calledCampaignOutcome,
  campaigns_connectFormIntegration: calledCampaignOutcome,
  campaigns_disconnectFormIntegration: calledCampaignOutcome,
  audiences_create: (content, siteId) => {
    const audienceId = createdAudienceId(content);
    if (audienceId === undefined) {
      return undefined;
    }
    return {
      events: [
        {
          domain: 'audiences',
          action: 'created',
          entityId: audienceId,
          siteId,
          source: 'agent',
          correlationId: correlationId(content),
        },
      ],
    };
  },
};

function campaignSettingsOutcome(content: unknown, siteId: number | undefined): ToolMutationOutcome | undefined {
  const campaignId = numericId(content);
  if (campaignId === undefined) {
    return undefined;
  }
  return {
    events: [campaignEvent('updated', campaignId, siteId, content, correlationId(content))],
  };
}

// The result names no campaign, so the id comes from the call's arguments, where Agents also accepts a numeric string.
function calledCampaignOutcome(
  _content: unknown,
  siteId: number | undefined,
  input: unknown,
): ToolMutationOutcome | undefined {
  if (input && typeof input === 'object') {
    const campaignId = (input as { campaignId?: unknown }).campaignId;
    const id = typeof campaignId === 'string' && /^\d+$/.test(campaignId) ? Number(campaignId) : campaignId;
    if (typeof id === 'number') {
      return { events: [campaignEvent('updated', id, siteId)] };
    }
  }
  return undefined;
}

function campaignStatusOutcome(
  content: unknown,
  siteId: number | undefined,
  action: 'published' | 'unpublished',
): ToolMutationOutcome | undefined {
  const campaignId = numericId(content);
  if (campaignId === undefined) {
    return undefined;
  }
  return {
    events: [campaignEvent(action, campaignId, siteId, undefined, correlationId(content))],
  };
}

function campaignEvent(
  action: EntityMutationEvent['action'],
  entityId: number,
  siteId: number | undefined,
  payload?: unknown,
  eventCorrelationId?: string,
): EntityMutationEvent {
  return {
    domain: 'campaigns',
    action,
    entityId,
    siteId,
    source: 'agent',
    payload,
    correlationId: eventCorrelationId,
  };
}

// publish / unpublish / updateSettings / updateTargeting / updateText results carry the campaign id at the top level.
function numericId(content: unknown): number | undefined {
  if (content && typeof content === 'object') {
    const id = (content as { id?: unknown }).id;
    if (typeof id === 'number') {
      return id;
    }
  }
  return undefined;
}

// The assistant changes a new campaign's content in the widget editor, so that is where it lands.
function campaignEditorOrSettings(campaign: { id: number; widgets?: unknown }): (string | number)[] {
  const firstWidget = Array.isArray(campaign.widgets)
    ? (campaign.widgets[0] as { widgetId?: unknown } | undefined)
    : undefined;
  return typeof firstWidget?.widgetId === 'number'
    ? ['/campaigns', campaign.id, 'widgets', firstWidget.widgetId, 'editor']
    : ['/campaigns', campaign.id, 'settings'];
}

// createFromTemplate / duplicateWidget results nest the full campaign settings view.
function nestedCampaign(content: unknown): { id: number; widgets?: unknown } | undefined {
  if (content && typeof content === 'object') {
    const campaign = (content as { campaign?: unknown }).campaign;
    if (numericId(campaign) !== undefined) {
      return campaign as { id: number; widgets?: unknown };
    }
  }
  return undefined;
}

function createdAudienceId(content: unknown): string | undefined {
  if (content && typeof content === 'object') {
    const { created, audienceId } = content as { created?: unknown; audienceId?: unknown };
    if (created === true && typeof audienceId === 'string') {
      return audienceId;
    }
  }
  return undefined;
}

interface CopyTarget {
  targetSiteId?: number;
  copiedCampaignId?: number;
  errorCode?: string;
}

function copyTargets(content: unknown): CopyTarget[] {
  if (content && typeof content === 'object') {
    const targets = (content as { targets?: unknown }).targets;
    if (Array.isArray(targets)) {
      return targets.filter((target): target is CopyTarget => target !== null && typeof target === 'object');
    }
  }
  return [];
}

// The copy result doesn't say whether the original was stopped, so this follows what the call asked for.
function stoppedSourceCampaignId(content: unknown, input: unknown): number | undefined {
  if (
    !input ||
    typeof input !== 'object' ||
    (input as { stopOriginalCampaign?: unknown }).stopOriginalCampaign !== true
  ) {
    return undefined;
  }
  if (content && typeof content === 'object') {
    const id = (content as { sourceCampaignId?: unknown }).sourceCampaignId;
    if (typeof id === 'number') {
      return id;
    }
  }
  return undefined;
}

function correlationId(content: unknown): string | undefined {
  if (content && typeof content === 'object') {
    const value = (content as { correlationId?: unknown }).correlationId;
    if (typeof value === 'string') {
      return value;
    }
  }
  return undefined;
}
